import React, { useMemo, useState } from 'react';
import { UseQueryResult } from 'react-query';
import { DocumentScanLlmTraceResponse } from 'common/lib/model/documentScan/DocumentScanTrace';
import Flex from 'src/components/Flex';
import LoadingIcon from 'src/components/LoadingIcon';
import ModalHeader from 'src/components/ModalHeader';
import SideModal from 'src/components/SideModal';
import TabNav from 'src/components/TabNav';
import { buildLlmTraceViewModels } from './llmTraceViewModel';
import styles from './LlmTraceModal.module.scss';

type TraceDetailPanel = 'formatted' | 'json';

function formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
        return 'N/A';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}

function formatDuration(value: any): string {
    const durationMs = Number(value);
    if (!Number.isFinite(durationMs)) {
        return 'N/A';
    }
    if (durationMs >= 1000) {
        return `${(Math.round(durationMs / 10) / 100).toFixed(2)}s`;
    }
    return `${Math.round(durationMs)}ms`;
}

function formatTokenCount(value: any): string {
    const count = Number(value);
    if (!Number.isFinite(count)) {
        return 'N/A';
    }
    return count.toLocaleString();
}

function formatTraceContent(content: any): string {
    if (Array.isArray(content)) {
        return content
            .map((item) => {
                if (item?.type === 'text') {
                    return item.text || '';
                }
                if (item?.type === 'image_reference') {
                    return [
                        'Image reference',
                        item.label ? `label: ${item.label}` : null,
                        item.documentScanAssetId ? `asset: ${item.documentScanAssetId}` : null,
                        item.mimeType ? `type: ${item.mimeType}` : null,
                    ]
                        .filter(Boolean)
                        .join(' / ');
                }
                return formatValue(item);
            })
            .filter(Boolean)
            .join('\n\n');
    }
    return formatValue(content);
}

function formatJsonIfPossible(value: any): string {
    if (typeof value !== 'string') {
        return formatValue(value);
    }
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
}

function formatVersionWithId(version: any, id: any): string {
    if (!version && !id) {
        return 'N/A';
    }

    const versionLabel = version ? `v${version}` : 'Version N/A';
    return id ? `${versionLabel} / ID: ${id}` : versionLabel;
}

export default function LlmTraceModal(props: {
    isOpen: boolean;
    onClose: () => void;
    traceQuery: Pick<UseQueryResult<DocumentScanLlmTraceResponse>, 'data' | 'isLoading' | 'error'>;
}) {
    const { isOpen, onClose, traceQuery } = props;
    const [selectedTracePanel, setSelectedTracePanel] = useState<TraceDetailPanel>('formatted');
    const traces = Array.isArray(traceQuery.data?.traces) ? traceQuery.data.traces : [];
    const traceViewModels = useMemo(() => buildLlmTraceViewModels(traces), [traces]);
    const traceDetailTabs = useMemo(
        () => [
            { key: 'formatted' as TraceDetailPanel, label: 'Formatted' },
            { key: 'json' as TraceDetailPanel, label: 'JSON' },
        ],
        [],
    );

    return (
        <SideModal isOpen={isOpen} isWide onClose={onClose}>
            <ModalHeader title="LLM Trace" onClose={onClose} />
            <div className={styles.traceModalBody}>
                {traceQuery.isLoading && (
                    <Flex align="center" justify="center" className={styles.loadingContent}>
                        <LoadingIcon />
                    </Flex>
                )}
                {traceQuery.error && <div className={styles.errorMessage}>Error loading LLM trace</div>}
                {!traceQuery.isLoading && !traceQuery.error && (
                    <>
                        <div className={styles.traceTabs}>
                            <TabNav
                                tabs={traceDetailTabs.map((tab) => tab.label)}
                                selectedTabIndex={traceDetailTabs.findIndex((tab) => tab.key === selectedTracePanel)}
                                onTabClick={(tabName: string) => {
                                    setSelectedTracePanel(
                                        traceDetailTabs.find((tab) => tab.label === tabName)?.key || 'formatted',
                                    );
                                }}
                            />
                        </div>

                        {selectedTracePanel === 'json' ? (
                            <pre className={styles.tracePre} data-test-id="llm-trace-json-view">
                                {JSON.stringify(traces, null, 2)}
                            </pre>
                        ) : (
                            <div className={styles.traceFormattedView} data-test-id="llm-trace-formatted-view">
                                {traces.length ? (
                                    traceViewModels.map((trace, traceIndex) => {
                                        const traceSummaryFields = [
                                            { label: 'Provider', value: trace.provider },
                                            { label: 'Model', value: trace.model },
                                            { label: 'Temperature', value: trace.temperature },
                                            {
                                                label: 'Structured output',
                                                value:
                                                    trace.structuredOutputEnabled === null
                                                        ? 'N/A'
                                                        : trace.structuredOutputEnabled
                                                        ? 'Enabled'
                                                        : 'Disabled',
                                            },
                                            { label: 'Schema', value: trace.structuredOutputSchemaName },
                                            { label: 'Total time', value: formatDuration(trace.durationMs) },
                                            {
                                                label: 'Prompt tokens',
                                                value: formatTokenCount(trace.usage?.prompt_tokens),
                                            },
                                            {
                                                label: 'Completion tokens',
                                                value: formatTokenCount(trace.usage?.completion_tokens),
                                            },
                                            {
                                                label: 'Total tokens',
                                                value: formatTokenCount(trace.usage?.total_tokens),
                                            },
                                            {
                                                label: 'Type version',
                                                value: formatVersionWithId(
                                                    trace.documentScanTypeVersionNumber,
                                                    trace.documentScanTypeVersionId,
                                                ),
                                            },
                                            {
                                                label: 'Override version',
                                                value: formatVersionWithId(
                                                    trace.facilityDocumentScanTypeVersionNumber,
                                                    trace.facilityDocumentScanTypeVersionId,
                                                ),
                                            },
                                            {
                                                label: 'Image assets',
                                                value: trace.imageAssetIds.join(', ') || 'N/A',
                                            },
                                            {
                                                label: 'OCR assets',
                                                value: trace.ocrAssetIds.join(', ') || 'N/A',
                                            },
                                        ];

                                        return (
                                            <section className={styles.traceCard} key={trace.key}>
                                                <div className={styles.traceCardHeader}>
                                                    <div>
                                                        <h3>Trace {traceIndex + 1}</h3>
                                                        <span>{trace.createdAt || 'N/A'}</span>
                                                    </div>
                                                    <div className={styles.traceMetric}>
                                                        <strong>{formatDuration(trace.durationMs)}</strong>
                                                        <span>response time</span>
                                                    </div>
                                                </div>

                                                {trace.unavailable && (
                                                    <div className={styles.errorMessage}>
                                                        {trace.errorMessage || 'This trace file is unavailable.'}
                                                    </div>
                                                )}

                                                <div className={styles.traceSummaryGrid}>
                                                    {traceSummaryFields.map((field) => (
                                                        <div className={styles.traceSummaryItem} key={field.label}>
                                                            <span>{field.label}</span>
                                                            <strong>{formatValue(field.value)}</strong>
                                                        </div>
                                                    ))}
                                                </div>

                                                {trace.requests.length ? (
                                                    trace.requests.map((requestTrace, requestIndex) => {
                                                        return (
                                                            <div
                                                                className={styles.traceRequestBlock}
                                                                key={`trace-request-${requestIndex}`}
                                                            >
                                                                <div className={styles.traceRequestHeader}>
                                                                    <h4>LLM request {requestIndex + 1}</h4>
                                                                    <div>
                                                                        <span>{requestTrace.provider || 'N/A'}</span>
                                                                        <span>{requestTrace.model || 'N/A'}</span>
                                                                        <span>
                                                                            {formatDuration(requestTrace.durationMs)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className={styles.traceConversation}>
                                                                    {requestTrace.messages.map(
                                                                        (message, messageIndex) => (
                                                                            <div
                                                                                className={styles.traceMessage}
                                                                                key={`${message.role}-${messageIndex}`}
                                                                            >
                                                                                <div
                                                                                    className={styles.traceMessageRole}
                                                                                >
                                                                                    {message.role ||
                                                                                        `Message ${messageIndex + 1}`}
                                                                                </div>
                                                                                <pre>
                                                                                    {formatTraceContent(
                                                                                        message.content,
                                                                                    )}
                                                                                </pre>
                                                                            </div>
                                                                        ),
                                                                    )}
                                                                    <div className={styles.traceMessage}>
                                                                        <div className={styles.traceMessageRole}>
                                                                            response
                                                                        </div>
                                                                        <pre>
                                                                            {formatJsonIfPossible(
                                                                                requestTrace.responseContent,
                                                                            )}
                                                                        </pre>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className={styles.typedEmpty}>
                                                        No LLM request payloads were recorded for this trace.
                                                    </div>
                                                )}
                                            </section>
                                        );
                                    })
                                ) : (
                                    <div className={styles.typedEmpty}>No LLM trace data was recorded.</div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </SideModal>
    );
}
